// Library entry. Exposes a global `window.AIWidget` API for explicit
// init, and auto-initializes when the loading <script> tag carries
// data-client-id + data-api-url + data-target attributes.
//
// Vite's IIFE build wraps everything in `var AIWidget = (function(){…})()`.
// The IIFE's return value becomes window.AIWidget (because top-level `var`
// in a non-module script creates a window property). With a *named* export
// like `export const Widget`, Vite returns a namespace `{ Widget }` and
// `window.AIWidget.init` doesn't exist — only `window.AIWidget.Widget.init`.
// Default export gets Vite to assign the API object directly to window.

import { createWidget } from "./mount";
import { trackHost } from "./session";
import type { InitOptions, WidgetHandle } from "./types";

const handles: WidgetHandle[] = [];

const Widget = {
  init(options: InitOptions): WidgetHandle {
    const handle = createWidget(options);
    handles.push(handle);
    return handle;
  },
  destroyAll(): void {
    while (handles.length) {
      const h = handles.pop();
      h?.destroy();
    }
  },
  // Host-page conversion bridge. The client's site calls
  // window.AIWidget.track('contact_form_submit', {...}) when an off-widget
  // conversion happens; it's recorded against this visit's chat session and
  // re-dispatched as a DOM 'aiwidget:conversion' event for the host's own
  // analytics. Requires a widget mounted on the page (see trackHost).
  track(type: string, metadata?: Record<string, unknown>): void {
    trackHost(type, metadata);
  },
};

export default Widget;

declare global {
  interface Window {
    AIWidget: typeof Widget;
  }
}

// === Auto-init from the loading script tag's data-* attributes ===
//
// `document.currentScript` is null inside ES module scripts, so we look
// up any script tag with a data-client-id attribute as a fallback. This
// lets the same code path work for both built IIFE bundles loaded with a
// regular <script src>, and dev-mode <script type="module"> loads.
function findConfigScript(): HTMLScriptElement | null {
  const direct = document.currentScript as HTMLScriptElement | null;
  if (direct?.dataset?.clientId) return direct;
  const candidates = document.querySelectorAll<HTMLScriptElement>(
    "script[data-client-id]",
  );
  return candidates[candidates.length - 1] ?? null;
}

function autoInit(): void {
  const script = findConfigScript();
  if (!script) return;
  const ds = script.dataset;
  if (!ds.clientId || !ds.apiUrl || !ds.target) return;

  const opts: InitOptions = {
    clientId: ds.clientId,
    apiUrl: ds.apiUrl,
    target: ds.target,
  };
  // brand-name, welcome-message, and starter-prompts intentionally NOT
  // read from data attributes — those are admin-only fields pulled at
  // runtime from /v1/config/:clientId so the operator can tune them
  // without re-pasting the install snippet on every client's site.
  // Theme attributes are initial-paint hints; the admin-saved values
  // arriving on /v1/config are applied after fetch and win.
  if (ds.accentColor) opts.accentColor = ds.accentColor;
  if (ds.accentForeground) opts.accentForeground = ds.accentForeground;
  if (ds.surfaceColor) opts.surfaceColor = ds.surfaceColor;
  if (ds.userBubbleColor) opts.userBubbleColor = ds.userBubbleColor;
  if (ds.cardActionTextColor) opts.cardActionTextColor = ds.cardActionTextColor;
  if (ds.sendIconColor) opts.sendIconColor = ds.sendIconColor;

  // The target element may not exist yet at script-eval time. Defer to
  // DOMContentLoaded if the document is still parsing.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => Widget.init(opts), {
      once: true,
    });
    return;
  }

  Widget.init(opts);
}

autoInit();
